import { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, fmtTime, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { activeWord, segText, speakerColor } from "./transcriptUtils";

const Highlight = ({ text, q }) => {
  if (!q) return text;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) => (i % 2 ? <mark key={i} className="rounded bg-amber-200 px-0.5 text-slate-900">{p}</mark> : p));
};

export const SegmentSpan = ({ s, version, current, q, onSeek }) => {
  const text = segText(s, version);
  const active = current >= s.start && current < s.end;
  if (!active || q) {
    return (
      <span data-seg={s.id} onClick={() => onSeek?.(s.start)} className="cursor-pointer rounded transition-colors hover:bg-violet-50" data-testid={`seg-span-${s.idx}`}>
        <Highlight text={text} q={q} />{" "}
      </span>
    );
  }
  const words = text.split(/\s+/).filter(Boolean);
  const k = activeWord(s, version, current, words);
  const exact = version === "raw" && s.words?.length === words.length;
  return (
    <span data-seg={s.id} className="rounded bg-violet-100/70 py-0.5" data-testid="active-segment">
      {words.map((w, i) => (
        <span key={i} onClick={() => onSeek?.(exact ? s.words[i][0] : s.start + ((s.end - s.start) * i) / words.length)}
          className={`cursor-pointer rounded px-[1px] transition-colors ${i === k ? "bg-violet-600 text-white" : ""}`}>{w} </span>
      ))}
    </span>
  );
};

export const SegmentRow = ({ s, meetingId, names, version, current, q, onSeek, showTs, canEdit, onSaved }) => {
  const { L } = useApp();
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try {
      await api.patch(`/meetings/${meetingId}/segments/${s.id}`, { text: edit.trim() });
      onSaved({ ...s, text: edit.trim() });
      setEdit(null);
      toast.success(L("Segmen disimpan", "Segment saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const active = current >= s.start && current < s.end;
  return (
    <div className={`group flex gap-3 rounded-xl px-3 py-2.5 transition-colors ${active ? "bg-violet-50 ring-1 ring-violet-200" : "hover:bg-slate-50"}`} data-testid={`transcript-segment-${s.idx}`}>
      {showTs && <button onClick={() => onSeek?.(s.start)} className="w-14 shrink-0 pt-0.5 text-left font-mono text-xs text-violet-700 hover:underline" data-testid={`segment-ts-${s.idx}`}>{fmtTime(s.start)}</button>}
      <div className="min-w-0 flex-1">
        <span className={`mb-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${speakerColor(s.speaker)}`}>{names[String(s.speaker)] || `Pembicara ${s.speaker}`}</span>
        {edit !== null ? (
          <div className="space-y-2">
            <Textarea value={edit} onChange={(e) => setEdit(e.target.value)} className="min-h-[70px] rounded-xl text-sm" data-testid={`segment-edit-input-${s.idx}`} />
            <div className="flex gap-2">
              <Button size="sm" className="h-8 rounded-lg bg-violet-600 hover:bg-violet-700" onClick={save} data-testid={`segment-save-btn-${s.idx}`}><Check size={14} className="mr-1" /> {L("Simpan", "Save")}</Button>
              <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => setEdit(null)} data-testid={`segment-cancel-btn-${s.idx}`}><X size={14} /></Button>
            </div>
          </div>
        ) : (
          <p className="text-[15px] leading-relaxed text-slate-700"><SegmentSpan s={s} version={version} current={current} q={q} onSeek={onSeek} /></p>
        )}
      </div>
      {canEdit && edit === null && (
        <button onClick={() => setEdit(s.text)} className="h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100 focus:opacity-100 sm:grid" aria-label={L("Edit segmen", "Edit segment")} data-testid={`segment-edit-btn-${s.idx}`}><Pencil size={14} /></button>
      )}
    </div>
  );
};
